import { CreditCardAttributesProps } from 'services/myAccount/myAccount.types';
import { ExtendedSlots } from 'services/providers/providers.types';
import { ResponseProps } from 'services/services.types';

import { OrchestrateState } from 'store/orchestrate-signup/orchestrate.types';
import { UserState } from 'store/user/user.types';

import { UserTypeProps } from 'contexts/messagesContext/messagesContext.types';
import { FlowTypes, Gender, PlanCodes, SexAtBirth } from 'utils/enums';

import { AppointmentCallMethod, AppointmentTime } from 'models/appointment.types';
import { Provider, ProviderWithSlots } from 'models/providers.types';
import { PhoneProps, UserProfile, WeightManagementProps } from 'models/user.types';

export interface LoginReqProps {
  email: string;
  password: string;
}

export type LoginResProps = ResponseProps<UserProfile>;

export interface PatientLoginReqProps {
  loginToken: string;
}

export type PatientLoginResProps = LoginResProps;

export interface SignUpReqProps {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
}
export type SignUpResProps = ResponseProps<Partial<UserState>>;

export type LogoutReqProps = string;

export interface LogoutResProps {
  code: number;
  message: string;
}

export interface GetProvidersForStateReqProps {
  flow?: FlowTypes;
  isInitialVisit?: boolean;
  isUnlimitedPlan?: boolean | 'unknown';
  limit: number;
  pageNo: number;
  state: string;
}

export interface GetProvidersForStateResProps extends Omit<ResponseProps<Provider[]>, 'info'> {
  info: {
    totalCount: number;
  };
}

export interface GetAvailableDatesReqProps {
  accessToken?: string;
  appointmentTypeId: string;
  endDate: string;
  excludeMyProvider?: boolean;
  isReschedule?: boolean;
  onboardingPartnerName?: 'HRT' | 'Optavia';
  planId: string;
  planPricePointId?: string;
  startDate: string;
  state: string;
  timezone: string;
}

export type GetAvailableDatesResProps = ResponseProps<
  {
    count: number;
    date: string;
  }[]
>;

export type HoldProviderResProps = ResponseProps<HoldProviderDataProps>;

interface HoldProviderDataProps {
  appointmentCreatedAt: string;
  bookedSlotId: string;
}

export interface HoldProviderReqProps {
  appointmentTime: AppointmentTime;
  appointmentTypeId: string;
  doctorId: string;
  planId: string;
  sessionId: string;
  state: string;
  timezone: string;
}

export interface CreateProspectUserReqProps {
  email: string;
  firstName?: string;
}

export type CreateProspectUserResProps = ResponseProps<CreateProspectUserDataProps>;

interface CreateProspectUserDataProps {
  status: string;
  userId: false;
}

export interface ForgotPasswordTokenReqProps {
  email: string;
}

export type ForgotPasswordTokenResProps = ResponseProps<unknown>;

export interface ForgotPasswordReqProps {
  password: string;
  token: string;
}

export type ForgotPasswordResProps = ResponseProps<unknown>;

export type ReleaseSlotsReqProps = string;

export type ReleaseSlotsResProps = ResponseProps<unknown>;

export interface CreateAccountWithPaymentReqProps {
  couponCode?: string;
  creditCardAttributes: CreditCardAttributesProps;
  doctorId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  planCode?: string;
  planId: string;
  planPricePointId: string;
  redirectUrl?: string;
}

export type CreateAccountWithPaymentResProps = ResponseProps<AccessTokenProps>;

interface AccessTokenProps {
  accessToken: string;
  customerId: string;
  refreshToken: string;
  subscriptionId: string;
}

export type VerifyUserTokenResProps = ResponseProps<UserProfile>;

export type VerifyUserTokenReqProps = { disableActiveUsers?: boolean; token: string };

export interface AddChargifyTokenReqProps {
  accessToken: string;
  appointmentPricePointId?: string;
  componentId?: string;
  couponCode?: string;
  creditCardAttributes: CreditCardAttributesProps;
  flow: string;
  planCode?: PlanCodes;
  planId?: string;
  planPricePointId: string;
}

export type AddChargifyTokenResProps = ResponseProps<AddChargifyTokenDataProps>;

interface AddChargifyTokenDataProps {
  _id: string;
  accessToken: string;
  activePlanId: string;
  customerId: string;
  doctorId: string;
  elationId: number;
  email: string;
  expiredToken: string;
  firstName: string;
  gender?: Gender;
  initialLoginVideo: string;
  isUnlimitedPlan: boolean;
  lastName: string;
  notificationPreferences: NotificationPreferencesProps;
  refreshToken: string;
  sexAtBirth: SexAtBirth;
  status: string;
  subscriptionId: string;
  timezone: string;
}

interface NotificationPreferencesProps {
  browserNotification: NotificationPreferencesItemProps;
  emailNotification: NotificationPreferencesItemProps;
  textNotification: NotificationPreferencesItemProps;
}

interface NotificationPreferencesItemProps {
  appointmentChanges: boolean;
  appointmentReminder: boolean;
  messages: boolean;
  newOffers: boolean;
}

interface PersonalInfoProps {
  accessToken?: string;
  address: string;
  city: string;
  dob: string;
  doctorId?: string;
  email: string;
  firstName: string;
  flow: FlowTypes | 'queue' | 'lifemd-plus';
  gender?: Gender;
  lastName: string;
  nickName?: string;
  password: string;
  phone: AddPersonalInfoPhoneProps;
  sessionId?: string;
  sexAtBirth: SexAtBirth;
  state: string;
  timezone: string;
  zipCode: string;
}

export interface AddPersonalInfoReqProps extends PersonalInfoProps {
  isAsapAppointment?: boolean;
  planCode?: string;
  planId?: string;
  planPricePointId?: string;
}

interface AddPersonalInfoPhoneProps {
  countryCode?: string;
  phoneNumber?: string;
}

export type AddPersonalInfoResProps = ResponseProps<CreateAccountResProps>;

export interface AddFirstAppointmentReqProps {
  accessToken: string;
  appointmentDescription?: string;
  appointmentMethod: AppointmentCallMethod;
  appointmentPricePointId?: string;
  appointmentTypeId: string;
  bookedSlotId?: string;
  componentId?: string;
  couponCode?: string;
  doctorId?: string;
  flow?: string;
  isAsapAppointment: boolean;
}

export type AddFirstAppointmentResProps = ResponseProps<AddFirstAppointmentDataProps>;

export interface AddFirstAppointmentDataProps {
  _id: string;
  appointmentDescription: string;
  appointmentMethod: 'video' | 'audio';
  appointmentTime?: AppointmentTime;
  appointmentType: AddFirstAppointmentAppointmentTypeProps;
  appointmentTypeId: string;
  bookedSlotId: string;
  categoryDisplayName: string;
  createdAt: string;
  doctorId: string;
  elationAppointmentId: string;
  executedTriggers: [];
  images: [];
  patientId: string;
  staffName: string;
  updatedAt: string;
}

interface AddFirstAppointmentAppointmentTypeProps {
  _id: string;
  displayName: string;
}

export interface GetProvidersResProps extends Omit<ResponseProps<ProviderWithSlots[]>, 'info'> {
  info: {
    totalCount: number;
  };
}

export interface GetProvidersReqProps {
  appointmentTypeId: string;
  date: string;
  isInitialVisit?: boolean;
  isUnlimitedPlan?: boolean | 'unknown';
  limit: number;
  pageNo: number;
  planId: string;
  state: string;
  timezone: string;
}

export type CreateAccountAfterPaymentReqProps = PersonalInfoProps;

interface CreateAccountResProps {
  _id: string;
  accessToken: string;
  activePlanId: string;
  appointmentCreatedAt: string;
  doctorId: string;
  email: string;
  firstName: string;
  hasSubscription: boolean;
  initialLoginVideo: string;
  isInitialLogin: boolean;
  isUnlimitedPlan: boolean;
  lastName: string;
  notificationPreferences: NotificationPreferencesProps;
  refreshToken: string;
  sexAtBirth: SexAtBirth;
  state: string;
  timezone: string;
  userType: { name: string; shortCode: string };
}

export interface GetProviderImagesReqProps {
  accessToken?: string;
}

export type GetProviderImagesResProps = ResponseProps<
  {
    displayName: string;
    profileImage: string;
  }[]
>;

export interface GetCombinedAvailabilityReqProps {
  accessToken?: string;
  appointmentDate: string;
  appointmentTypeId: string;
  doctorLicenseState: string;
  patientTimezone: string;
  planId: string;
  planPricePointId?: string;
  sessionId: string;
}
export type GetCombinedAvailabilityResProps = ResponseProps<ExtendedSlots>;

export interface CheckIfAsapOptionReqProps {
  appointmentTypeId: string;
  planCode: string;
  state: string;
  timezone: string;
}
export type CheckIfAsapOptionResProps = ResponseProps<{
  isQueueAvailable: boolean;
  scheduledTime?: string;
  waitTime: string;
}>;
type CampaignInfo = {
  __v: number;
  _id: string;
  createdAt: string;
  recId: string;
  settings: { plans: { [key: string]: string | number | boolean | null }[] };
  status: boolean;
  updatedAt: string;
};
export type GetCampaignInfoResProps = ResponseProps<CampaignInfo>;

interface ShortTermTokenResProps {
  expiresAt: string;
  expiresIn: string;
  token: string;
}
export type GetShortTermTokenResProps = ResponseProps<ShortTermTokenResProps>;

export interface PaymentCombinedReqProps {
  accessToken: string;
  appointment: Omit<AddFirstAppointmentReqProps, 'accessToken'>;
  credit_card: CreditCardAttributesProps;
  flow: FlowTypes | 'lifemd-plus';
  subscription: {
    appointmentPricePointId?: string;
    componentId?: string;
    couponCode?: string;
    planCode?: PlanCodes;
    planId?: string;
    planPricePointId: string;
  };
}

export type PaymentCombinedResProps = ResponseProps<{
  appointmentData: AddFirstAppointmentDataProps;
  userData: AddChargifyTokenDataProps;
}>;

export enum IdpAuthSteps {
  /* Patient signs in with a new IDP, needs to complete additional signin steps. */
  CONFIRM_SIGNIN = 'confirm-signin',
  /* Patient signs up with IDP, needs to complete additional signup steps. */
  CONFIRM_SIGNUP = 'confirm-signup',
  /* Patient signed in with IDP successfully. */
  SIGNIN_SUCCESS = 'signin-success',
  /* Patient signs in with a new IDP (has other IDPs connected), but does not have an account password to confirm it. */
  SIGNIN_UNAVAILABLE_USE_ANOTHER_IDP = 'signin-unavailable-use-another-idp',
  /* Patient signed up with IDP successfully. */
  SIGNUP_SUCCESS = 'signup-success'
}

interface IdpUserRes {
  _id: string;
  accessToken: string;
  activePlanCode: string;
  activePlanId: string;
  activePricePoint: string;
  brazeExternalId: string;
  customerId: string;
  doctorId: string;
  elationId: string;
  email: string;
  firstName: string;
  hasPasswordResetToken: boolean;
  hasSubscription: boolean;
  idp: string;
  initialLoginVideo: string;
  isInitialLogin: boolean;
  isUnlimitedPlan: boolean;
  lastLogin: string;
  lastLoginClientPlatform: string | null;
  lastName: string;
  notificationPreferences: NotificationPreferencesProps;
  paymentProfiles: {
    chargifyPaymentProfileId: string;
    expirationMonth: string;
    expirationYear: string;
    isDefault: boolean;
    paymentMethod: string;
  }[];
  phone: PhoneProps;
  refreshToken: string;
  reviewedProviders: string[];
  sexAtBirth: string;
  state: string;
  status: string;
  subscriptionId: string;
  timezone: string;
  userType: UserTypeProps;
  weightManagement: WeightManagementProps;
}

export type SignInWithProviderResProps = ResponseProps<{
  email?: string;
  sessionId?: string;
  step: IdpAuthSteps;
  user?: IdpUserRes & UserProfile;
}>;
export type SignInWithGoogleResProps = SignInWithProviderResProps;
export type SignInWithGoogleReqProps = { authorizationCode: string };

export type SignInWithAppleResProps = SignInWithProviderResProps;
export type SignInWithAppleReqProps = { authorizationCode: string };

export type ConfirmSignUpResProps = ResponseProps<{
  step: IdpAuthSteps;
  user?: UserProfile;
}>;
export type ConfirmSignUpReqProps = { firstName: string; lastName: string; sessionId?: string };

export type ConfirmSignInResProps = ConfirmSignUpResProps;
export type ConfirmSignInReqProps = { password: string; sessionId?: string };

export type SendVerifyEmailReqProps = {
  accessToken: string;
};
export type SendVerifyEmailResProps = ResponseProps<void>;

export type ChangePasswordSendCodeResProps = ResponseProps<void>;
export type ChangePasswordSendCodeReqProps = string;

export type ChangePasswordVerifyCodeResProps = ResponseProps<void>;
export type ChangePasswordVerifyCodeReqProps = {
  accessToken: string;
  code: string;
};

export type ChangePasswordResProps = ResponseProps<void>;
export type ChangePasswordReqProps = {
  accessToken: string;
  code: string;
  newPassword: string;
};

type CheckoutState = {
  _id: string;
  accessToken: string;
  chargifyCustomerId: number;
  chargifyPaymentProfiles: {
    chargifyPaymentProfileId: number;
    isDefault: boolean;
    paymentMethod: string;
  }[];
  chargifySubscriptionId: number;
  expiredToken: string;
  refreshToken: string;
};

export type SendOrchestrateFormReqProps = Partial<OrchestrateState>;
export type SendOrchestrateFormResProps = ResponseProps<OrchestrateState | CheckoutState>;

export type LabItem = {
  address: string;
  displayName: string;
  googleMapsUri: string;
  labType: LabsType;
  location: {
    latitude: number;
    longitude: number;
  };
  openHours?: string[];
  phone: string | null;
  website: string | null;
};

export type LabsType = 'labcorp' | 'quest';
export type GetLabsReqProps = { labType: LabsType; zipCode: string };
export type GetLabsResProps = ResponseProps<{ labs: LabItem[] }>;

export type StatesWhereAshIsAvailable = ResponseProps<{ states: string[] }>;

export type CreateProspectAccountReqProps = {
  address?: {
    city: string;
    state: string;
    streetLine: string;
    zipCode: string;
  };
  dob?: string;
  email: string;
  firstName: string;
  lastName: string;
  password?: string;
  phone?: string;
  repeatPassword?: string;
  termsAndConditionsAccepted: boolean;
};
export type CreateProspectAccountResProps = ResponseProps<
  Partial<CreateProspectAccountReqProps>,
  {
    accessToken?: string;
    refreshToken?: string;
  }
>;
